import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BaseInput from '../../components/form/BaseInput.jsx';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import AdvancedSearchFilterOrganizations from '../../components/misc/AdvancedSearchFilterOrganizations.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import {
    useGetOrganizationOwnershipTypesQuery,
    useLazyOrganizationsAdvancedSearchQuery
} from '../../api/organizations.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { Link, useLocation } from 'react-router-dom';
import circle from '../../resources/images/circle.png';
import { renderFieldWithHighlighting } from '../../utils/highlight.js';
import { useGetOrganizationCountriesQuery } from '../../api/misc.js';
import { exportToExcel } from '../../utils/helper.js';

const AdvancedSearchOrganizations = () => {
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const { user } = useCurrentUser();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [initialRender, setInitialRender] = useState(true);
    const [organizations, setOrganizations] = useState([]);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        industry: '',
        countries: [],
        founded: '',
        ownership: '',
        employees: { min: '', max: '' },
        revenue: { min: '', max: '' },
        profit: { min: '', max: '' },
        expenses: { min: '', max: '' }
    });
    const [applied, setApplied] = useState(false);
    const debouncedSearch = useDebounce(search, 500);
    const [showFilters, setShowFilters] = useState(false);

    const lastSearchRef = useRef('');

    useEffect(() => {
        if (location.state) {
            if (location.state.filters) {
                setFilters((prevFilters) => ({
                    ...prevFilters,
                    ...location.state.filters
                }));
            }
            if (location.state.search) {
                setSearch(location.state.search);
            }
            setInitialRender(false);
            setApplied(true);
        }
    }, [location.state]);

    const { data: ownershipTypes, isLoading: ownershipTypesLoading } = useGetOrganizationOwnershipTypesQuery();

    const ownershipTypesList = useMemo(() => {
        return Array.isArray(ownershipTypes?.response?.ownership_types)
            ? ownershipTypes.response.ownership_types.map((type) => ({
                  label: type.label,
                  value: type.value
              }))
            : [];
    }, [ownershipTypes]);

    const { data: countriesData } = useGetOrganizationCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.organization_countries) {
            return countriesData.response.organization_countries.map((country) => ({
                label: country.name,
                value: country.id
            }));
        }

        return [];
    }, [countriesData]);

    const resetSearchAndFilters = () => {
        setSearch('');
        setFilters({
            industry: '',
            countries: [],
            founded: '',
            ownership: '',
            employees: { min: '', max: '' },
            revenue: { min: '', max: '' },
            profit: { min: '', max: '' },
            expenses: { min: '', max: '' }
        });
        setOrganizations([]);
    };

    const countValid = Object.values(filters).reduce((count, value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? count + 1 : count;
        } else if (typeof value === 'object' && value !== null) {
            return value.min || value.max ? count + 1 : count;
        } else {
            return value ? count + 1 : count;
        }
    }, 0);

    const changeFilter = (field, subField, value) => {
        setFilters((prevFilters) => {
            if (subField) {
                return {
                    ...prevFilters,
                    [field]: {
                        ...prevFilters[field],
                        [subField]: value
                    }
                };
            }
            return {
                ...prevFilters,
                [field]: value
            };
        });
    };

    const formatRangeParam = (min, max) => {
        if (min && max) return `${min},${max}`;
        if (min) return `${min},-1`;
        if (max) return `-1,${max}`;
        return '';
    };

    const requestParams = useMemo(() => {
        const privateOrg = organizationSwitch[user?.id]?.private || null;
        const selectedOrganizations = Object.keys(organizationSwitch[user?.id]?.organizations || {}).filter(
            (key) => organizationSwitch[user?.id].organizations[key]
        );

        const employeesRange = formatRangeParam(filters.employees.min, filters.employees.max);
        const revenueRange = formatRangeParam(filters.revenue.min, filters.revenue.max);
        const profitRange = formatRangeParam(filters.profit.min, filters.profit.max);
        const expensesRange = formatRangeParam(filters.expenses.min, filters.expenses.max);

        return {
            ...(debouncedSearch !== '' ? { search: debouncedSearch } : {}),
            ...(filters.industry ? { industry: filters.industry } : {}),
            ...(filters.countries.length > 0 ? { countries: filters.countries } : {}),
            ...(filters.founded ? { founded: String(filters.founded) } : {}),
            ...(employeesRange ? { employees: employeesRange } : {}),
            ...(revenueRange ? { revenue: revenueRange } : {}),
            ...(profitRange ? { profit: profitRange } : {}),
            ...(expensesRange ? { expenses: expensesRange } : {}),
            ...(filters.ownership ? { ownership: filters.ownership } : {}),
            ...(selectedOrganizations.length > 0 ? { organizations: selectedOrganizations } : {}),
            ...(privateOrg ? { private: privateOrg } : {})
        };
    }, [organizationSwitch, debouncedSearch, filters, user?.id]);

    const [advancedSearch] = useLazyOrganizationsAdvancedSearchQuery();

    const applyFilters = useCallback(async () => {
        if (requestParams.private || requestParams.organizations?.length > 0) {
            setIsLoading(true);
            const result = await advancedSearch(requestParams);
            if (result?.data?.response?.organizations) {
                setOrganizations(result.data.response.organizations);
            }
            setApplied(false);
            setIsLoading(false);
        }
    }, [advancedSearch, requestParams]);

    const exportExcel = () => {
        let data = organizations.map((org) => {
            return {
                Name: org.name,
                Domain: org.domain,
                Employees: org.employees,
                Expenses: org.expenses,
                Founded: org.founded,
                Ownership: org.ownership,
                'Profit Margin': org.profit,
                Revenue: org.revenue,
                Liabilities: org.liabilities,
                Assets: org.assets,
                Industry: org.industry,
                Headquarters: org.headquarters?.name || '',
                Needs: (org.needs || []).map((n) => n.name).join(', '),
                'Aggregated Needs': (org.aggregated_needs || []).map((n) => n.name).join(', '),
                Capabilities: (org.capabilities || []).map((c) => c.name).join(', '),
                'Aggregated Capabilities': (org.aggregated_capabilities || []).map((c) => c.name).join(', ')
            };
        });

        exportToExcel(data, 'organizations.xlsx');
    };

    useEffect(() => {
        const currentRequest = JSON.stringify({ search, filters });
        if (
            ((debouncedSearch !== '' && debouncedSearch === search) || (countValid > 0 && applied)) &&
            lastSearchRef.current !== currentRequest
        ) {
            lastSearchRef.current = currentRequest;
            setInitialRender(false);
            applyFilters().then(() => {
                if (countValid === 0) {
                    setInitialRender(true);
                }
            });
        }
    }, [applyFilters, debouncedSearch, search, countValid, applied, filters]);

    const renderFilters = [
        {
            field: 'founded',
            type: 'yearpicker',
            title: 'Year Founded'
        },
        {
            field: 'ownership',
            type: 'dropdown',
            title: 'Ownership',
            options: ownershipTypesList,
            placeholder: 'Select Ownership'
        },
        {
            field: 'countries',
            type: 'multiselect',
            title: 'Headquarters',
            options: countries,
            placeholder: 'Select Headquarters'
        },
        ...['industry'].map((field) => ({
            field,
            type: 'text',
            title: field.charAt(0).toUpperCase() + field.slice(1),
            placeholder: `Enter ${field}`
        })),
        ...['employees', 'revenue', 'profit', 'expenses'].map((field) => ({
            field,
            type: 'range',
            title: field.charAt(0).toUpperCase() + field.slice(1),
            placeholderMin: `Min ${field}`,
            placeholderMax: `Max ${field}`
        }))
    ];

    const renderOrganizationCards = () => {
        return (
            <div className='mb-6'>
                <BaseCard className='p-6 bg-white shadow-lg rounded-4xl border-0'>
                    <h2 className='text-theme1-text1 font-bold text-2xl mb-4'>Organizations</h2>
                    {organizations.length > 0 ? (
                        <>
                            <p className='text-theme1-text2 mb-4'>
                                {search !== '' ? (
                                    <>
                                        {organizations.length} organization(s) mentioning "{search}"
                                    </>
                                ) : (
                                    <>found {organizations.length} organization(s) through filters</>
                                )}
                            </p>
                            {organizations.map((org) => (
                                <div key={org.id} className='border-t border-theme1-grey py-4'>
                                    <Link
                                        to={`/organizations/${org.id}`}
                                        className='text-lg text-theme1-success font-bold'
                                    >
                                        {renderFieldWithHighlighting(org.name, 'name', org.matched_fields)}
                                    </Link>
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className='text-gray-500'>No organizations found.</div>
                    )}
                </BaseCard>
            </div>
        );
    };

    return (
        <AuthLayout title='Advanced Search' backRoute={location.state?.backRoute || '/organizations/list'}>
            <BaseCard className='border-0 m-0 mb-6 -mt-6 p-4 pt-10 rounded-t-none bg-theme1-white shadow-lg max-w-3xl w-full mx-auto'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-between items-center mt-6 mb-4'>
                        <div className='flex-grow'>
                            <BaseInput
                                name='search'
                                type='text'
                                value={search}
                                placeholder='Search... (min 3 characters)'
                                setValue={setSearch}
                            />
                        </div>
                    </div>
                </div>
                <div className='mb-6 px-4 text-right'>
                    <div className='flex items-center w-full text-theme1-primary2'>
                        <div className='flex items-center space-x-3'>
                            <div className='flex items-center space-x-1'>
                                <BaseButton
                                    className='flex bg-theme1-lightBtns text-theme1-white p-3 mr-3 pb-[10px]'
                                    onClick={() => setShowFilters(true)}
                                >
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='23'
                                        height='23'
                                        viewBox='0 0 20 20'
                                        fill='none'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M4.09254 3.2116C4.17673 3.18208 4.26531 3.16699 4.35454 3.16699H14.6462C14.7354 3.16699 14.824 3.18208 14.9082 3.2116C15.1362 3.29158 15.3429 3.42268 15.5125 3.59487C15.682 3.76706 15.8099 3.97578 15.8863 4.20502C15.9627 4.43426 15.9856 4.67795 15.9533 4.91743C15.921 5.15691 15.8343 5.38581 15.6999 5.5866C15.6782 5.61903 15.6541 5.64981 15.6278 5.67869L11.8754 9.80639V15.042C11.8754 15.3419 11.706 15.616 11.4377 15.7501C11.1695 15.8842 10.8486 15.8552 10.6087 15.6753L7.44204 13.3003C7.24269 13.1508 7.12537 12.9162 7.12537 12.667V9.80639L3.37292 5.67869C3.34666 5.64981 3.32258 5.61903 3.30087 5.5866C3.16643 5.38581 3.07973 5.15691 3.04742 4.91743C3.01511 4.67796 3.03804 4.43427 3.11445 4.20502C3.19087 3.97578 3.31874 3.76706 3.48828 3.59487C3.65781 3.42268 3.86451 3.29157 4.09254 3.2116ZM4.66876 4.75033L8.50282 8.96779C8.6353 9.11352 8.7087 9.30339 8.7087 9.50033V12.2712L10.292 13.4587V9.50033C10.292 9.30339 10.3654 9.11352 10.4979 8.96779L14.332 4.75033H4.66876Z'
                                            fill='white'
                                        />
                                    </svg>
                                    <span className='text-sm text-theme1-white px-2'>
                                        {countValid} filter(s) applied
                                    </span>
                                </BaseButton>
                            </div>
                        </div>
                        <div className='ml-auto flex items-center space-x-3'>
                            {organizations && organizations.length > 0 && (
                                <BaseButton
                                    className='w-auto rounded-4xl border font-normal px-4 py-3 btn-red-outline'
                                    onClick={exportExcel}
                                >
                                    Export Excel
                                </BaseButton>
                            )}
                            <BaseButton
                                className='w-auto rounded-4xl border font-normal px-4 py-3 btn-red-outline'
                                onClick={() => resetSearchAndFilters()}
                            >
                                Reset
                            </BaseButton>
                        </div>
                    </div>
                </div>

                <AdvancedSearchFilterOrganizations
                    filters={filters}
                    setVisible={setShowFilters}
                    visible={showFilters}
                    config={renderFilters}
                    changeFilter={changeFilter}
                    applyFilters={applyFilters}
                    setApplied={setApplied}
                    loadingOwnership={ownershipTypesLoading}
                />
            </BaseCard>
            <div className='max-w-3xl w-full mx-auto px-4'>
                <div className='mb-6'>
                    {isLoading ? (
                        <ThreeDotsLoader />
                    ) : organizations.length === 0 && !isLoading ? (
                        <div className='py-12 flex flex-col justify-center items-center'>
                            <div className='text-2xl text-theme1-primary2 font-light max-w-80 text-center'>
                                {countValid === 0 && search === ''
                                    ? 'Search and/or filter to display organizations!'
                                    : 'No organizations found!'}
                            </div>
                            <img alt='No organizations' src={circle} className='my-8 w-40 h-40' />
                        </div>
                    ) : (
                        renderOrganizationCards()
                    )}
                </div>
            </div>
        </AuthLayout>
    );
};

export default AdvancedSearchOrganizations;
